import _ from 'lodash';
import axios from 'axios';
import env from '../util/env';
import {serviceURL, HttpStatusCodes, reducerOperations, counterOperation} from '../util/uiconstants';
import qs from 'query-string';
import {openDB} from 'idb';
import {ADD_DUPLICATE_DOCUMENT} from '../actions/actiontypes';
import {addDocumentToGLAccount} from '../actions/GLAccount/glAccountDocumentActions';
import {modifyGLAccountDocument} from '../actions/TrialBalances/trialbalanceactions';
import * as DocumentActions from '../actions/Document/documentactions';
import * as DocumentExtensionActions from '../actions/Document/documentextensionactions';
import {updateGLAccountUploadingCount} from '../actions/Document/duplicatedocumentsactions';
import stringHash from 'string-hash';

/**
 * DocHelper Middleware
 * This will handle the api calls to download document
 * If app is online it will set the docHelper online state to true
 * If app is offline it will set the online state to false and download the document by making an api call to docHelper
 * We are using this middleware in offline mode because api_middleware will skip the document download as action.isOnline === false
 * @param {Object} config - config.
 * @returns {Object} next action.
 */
const docHelperMiddleware = (config) => (store) => (next) => (action) => {
	const API_ERRORS = 'API_ERRORS';
	// Middleware should be executed only for docHelper calls
	if (!(action.meta && action.meta.docHelper)) {
		return next(action);
	}

	/*
	 * The below set of code is used to access IndexedDB using idb pacakge
	 */
	const ONLINE_DB = 'ONLINE_DB',
		OFFLINE_DB = 'OFFLINE_DB';

	const dbPromise = openDB('canvas-idb', 1, {
		upgrade(db) {
			db.createObjectStore(ONLINE_DB);
			db.createObjectStore(OFFLINE_DB);
		}
	});

	const idbKeyval = {
		async get(key) {
			return (await dbPromise).get(ONLINE_DB, key);
		},
		async set(key, val) {
			return (await dbPromise).put(ONLINE_DB, val, key);
		},
		async getOffline(key) {
			return (await dbPromise).get(OFFLINE_DB, key);
		},
		async setOffline(key, val) {
			return (await dbPromise).put(OFFLINE_DB, val, key);
		},
		async delete(key) {
			return (await dbPromise).delete(ONLINE_DB, key);
		},
		async clear() {
			return (await dbPromise).clear(ONLINE_DB);
		},
		async keys() {
			return (await dbPromise).getAllKeys(ONLINE_DB);
		}
	};

	/**
	 * Dispatches required actions to save the reason for change + rationale text for documents uploaded after the engagement report release date.
	 * @param {Object} docData document object.
	 * @param {Object} message or response from SignalR Hub. It should contain FileId(DocumentId), FileName, EngagementId.
	 */
	const saveDocumentChangeReason = function(docData, message) {
		if (docData.isUploadAfterRRD) {
			const engagementId = docData.engagementId;
			const documentId = docData.documentId || message.FileId;

			const updateDocumentAction = DocumentActions.updateDocument(engagementId, documentId, {
				documentChangeTypeId: docData.documentChangeTypeId
			});

			let getDocumentExtensionsAction = DocumentExtensionActions.getAllDocumentExtensions(engagementId, documentId);

			// 1. Search for documentExtension data for the uploaded document
			// 2.1. If nothing was found, send a createDocExtension action
			// 2.2. If there is data for that docId, send a updateDocExtension action
			store.dispatch(getDocumentExtensionsAction).then(() => {
				let docExtensions = store.getState().documentExtensions;
				if (docExtensions && Array.isArray(docExtensions)) {
					let documentExtension = docExtensions.find((de) => de.documentId === documentId);

					let saveChangeCommentAction;

					if (documentExtension) {
						saveChangeCommentAction = DocumentExtensionActions.updateDocumentChangeComment(
							engagementId,
							documentId,
							documentExtension.id,
							{
								changeReason: docData.rationaleText
							}
						);
					} else {
						saveChangeCommentAction = DocumentExtensionActions.createDocumentChangeComment(engagementId, documentId, {
							changeReason: docData.rationaleText
						});
					}

					store.dispatch(updateDocumentAction).then(() => store.dispatch(saveChangeCommentAction));
				}
			});
		}
	};

	/**
	 * Client Request Document Association dispatches the action to associate uploaded document to client Request
	 * This has been moved into the middleware so that it can be called after the uploadComplete Hub method is called.
	 * @param {Object} docData Object which contains taskId, taskTypeId, documentType, fileExt, workspaceId, parentName
	 * parentName is in which category the document is uploaded from evidence/temporary files/prior year evidence etc.
	 * @param {Object} message or response from SignalR Hub. It should contain FileId(DocumentId), FileName, EngagementId.
	 */
	const clientRequestDocumentAssociation = function(docData, message, userName) {
		if (!docData.relatedEntityId) {
			return;
		}

		// Few of the required properties are picked up from Dochelper message and other from docHelperDocuments store prop
		let currentEngagementId = store.getState().engagement.engagementId;

		let requestUrl =
			env.getBaseURL(serviceURL, currentEngagementId) +
			'ClientRequests/' +
			docData.relatedEntityId +
			'/ClientRequestDocuments';

		let data = {
			ClientRequestId: docData.relatedEntityId, // client request id
			DocumentId: message.FileId,
			DocumentTypeId: docData.documentType,
			DocumentName: message.FileName,
			Documentfileext: docData.fileExt,
			EngagementId: currentEngagementId,
			SourceEngagementID: message.EngagementId,
			workspaceId: docData.workspaceId,
			taskTypeId: docData.taskTypeId,
			parentName: docData.parentName,
			operationType: 1
		};

		// This will always be a client request document association (POST) as this is on upload Complete
		store.dispatch({
			type: 'ASSOCIATE_EXISTING_DOCUMENT',
			meta: {
				api: {
					url: requestUrl,
					method: 'POST',
					data: {
						EngagementId: data.EngagementId,
						documentId: data.DocumentId,
						targetDocumentTypeId: data.DocumentTypeId
					}
				}
			},
			documentName: message.FileName,
			userName: userName
		});
	};

	/**
	 * Task Document Association dispatches the action to associate uploaded document to task
	 * This has been moved into the middleware so that it can be called after the uploadComplete Hub method is called.
	 * @param {Object} docData Object which contains taskId, taskTypeId, documentType, fileExt, workspaceId, parentName
	 * parentName is in which category the document is uploaded from evidence/temporary files/prior year evidence etc.
	 * @param {Object} message or response from SignalR Hub. It should contain FileId(DocumentId), FileName, EngagementId.
	 */
	const taskDocumentAssociation = function(docData, message) {
		store.dispatch(updateGLAccountUploadingCount(counterOperation.increment));
		if (!docData.taskId) {
			return;
		}

		// Few of the required properties are picked up from Dochelper message and other from docHelperDocuments store prop
		let currentEngagementId = store.getState().engagement.engagementId;

		let requestUrl = env.getBaseURL(serviceURL, currentEngagementId) + 'TaskDocuments';

		let data = {
			TaskId: docData.taskId,
			DocumentId: message.FileId,
			DocumentTypeId: docData.documentType,
			DocumentName: message.FileName,
			Documentfileext: docData.fileExt,
			EngagementId: currentEngagementId,
			SourceEngagementID: message.EngagementId,
			workspaceId: docData.workspaceId,
			taskTypeId: docData.taskTypeId,
			parentName: docData.parentName,
			operationType: 1
		};

		// This will always be a task document association (POST) as this is on upload Complete
		store
			.dispatch({
				type: 'ASSOCIATE_TASK_DOCUMENTS',
				meta: {
					api: {
						url: requestUrl,
						method: 'POST',
						data: {
							EngagementId: data.EngagementId,
							documentId: data.DocumentId,
							taskIds: [data.TaskId],
							targetDocumentTypeId: data.DocumentTypeId,
							parentName: data.parentName,
							operationType: 1
						}
					}
				},
				documentName: message.FileName
			})
			.then(() => {
				store.dispatch(updateGLAccountUploadingCount(counterOperation.decrement));
				// if the parent is GLAccount then associate the first document to GLAccount
				if (docData.parentName.toLowerCase().includes('glaccount')) {
					associateGLAccount(docData);
				}
			})
			.finally(() => {
				store.dispatch(updateGLAccountUploadingCount(counterOperation.decrement));
			});
	};

	const offlineTaskDocumentAssociation = (data) => {
		let currentDocs = [];
		let associateActions = [];
		let requestUrl;
		let taskArr = [];

		data.forEach((doc) => {
			taskArr = [];
			taskArr.push({id: doc.TaskId, taskTypeId: doc.taskTypeId});

			let currentDoc = {
				id: doc.DocumentId,
				documentName: doc.DocumentName,
				fileExt: doc.Documentfileext,
				documentTypeId: doc.DocumentTypeId,
				engagementId: doc.EngagementId,
				approvals: [],
				documentReviews: [],
				clientApprovals: [],
				tasks: taskArr,
				parentName: doc.parentName,
				TaskId: doc.TaskId,
				operationType: 1
			};

			currentDocs.push(currentDoc);

			requestUrl = env.getBaseURL(serviceURL, doc.EngagementId) + 'TaskDocuments';

			let associateAction = {
				type: 'ASSOCIATE_TASK_DOCUMENTS',
				meta: {
					api: {
						url: requestUrl,
						method: 'POST',
						data: {
							EngagementId: doc.EngagementId,
							documentId: doc.DocumentId,
							taskIds: [doc.TaskId],
							targetDocumentTypeId: doc.DocumentTypeId,
							DocumentName: doc.DocumentName,
							DocumentTypeId: doc.DocumentTypeId,
							Documentfileext: doc.Documentfileext,
							DocumentId: doc.DocumentId,
							SourceEngagementID: doc.SourceEngagementID,
							approvals: doc.approvals || [],
							parentName: doc.parentName,
							TaskId: doc.TaskId,
							operationType: 1
						}
					}
				},
				documentName: doc.DocumentName
			};

			associateActions.push(associateAction);
		});

		console.log('📴📴 new uploaded Evidences', currentDocs);
		(async function updateOfflineEvidences() {
			console.log('📴📴updating offlineEvidences', currentDocs);

			let engagementIdFragment =
				action.meta.api.url.toLowerCase().indexOf('/engagements/') > -1
					? action.meta.api.url.toLowerCase().split('/engagements/')[1]
					: action.meta.api.url.toLowerCase().split('engagementid=')[1];
			let engagementId = engagementIdFragment.split('/')[0].split('&')[0];

			let offlineDocs = await idbKeyval.get(engagementId + '_' + 'offlineEvidences');
			console.log('📴📴📴offlineDOcs', offlineDocs);
			await idbKeyval.set(engagementId + '_' + 'offlineEvidences', {
				documents: [...offlineDocs.documents, ...currentDocs]
			});

			const currentTaskId = _.get(taskArr[0], 'id');
			const offlineRequestUrl = (requestUrl + '_' + currentTaskId).toLowerCase() + 'POST';
			let _id = stringHash(offlineRequestUrl);
			console.log('Calculating Hash For Url in updateOfflineEvidences: ', `URL - ${offlineRequestUrl}, Hash - ${_id}`);
			let offlinePostActions = await idbKeyval.getOffline(_id);
			let syncKeys = (await idbKeyval.getOffline('sync_keys')) || [];
			console.log('📴📴📴offlineDOcs', offlinePostActions);
			if (offlinePostActions && offlinePostActions.length) {
				await idbKeyval.setOffline(_id, [...offlinePostActions, ...associateActions]);
			} else {
				await idbKeyval.setOffline(_id, [...associateActions]);
			}
			// Adding the new key _id to the sync_keys record in offline_db
			// This is added as a workaround for Edge browser: Unsupported getAll indexedDB API.
			await idbKeyval.setOffline('sync_keys', _.union(syncKeys, [_id]));

			console.log('📴📴📴offlineDOcs', await idbKeyval.getOffline(_id));
		})().then(() => {
			console.log('Document added to offline Evidence in Relate files folder');
		});
	};

	/**
	 * This is a signalR hub client method, which is triggered after the Upload is Complete
	 * @param {Object} message - This is passed by SignalR Hub
	 * @returns {Object} the next action.
	 */
	const uploadComplete = function(message) {
		if (Number(message?.EngagementId) !== Number(store.getState().engagement?.engagementId)) {
			return;
		}
		console.log('📃: Upload Complete ✔', message);
		let updatedAction = Object.assign({}, action, {
			type: 'DOCUMENT_UPLOAD_COMPLETE',
			meta: {
				docHelper: {
					payload: Object.assign({}, action.meta.docHelper.payload, {
						documentId: message.FileId,
						inProgress: false
					}),
					message: message
				}
			}
		});

		const isUserUpload = updatedAction.meta.docHelper?.payload.isUserUpload;
		if (updatedAction.meta.docHelper) {
			updatedAction.meta.docHelper.payload.isUserUpload = false;
		}

		// use docHelperDocuments to perform task association as the user might navigate to different task
		// docHelperDocuments will have all the details of the taskId to which the association has to be performed.
		// for open, edit and save document we have FileName + "_" + FileId as key in store for which we don't need taskdocument association
		let docHelperDocumentData = store.getState().docHelperDocuments;
		let docData = docHelperDocumentData[message.FileName];

		//below method will dispatch task document association action with the details of task and document

		if (action.meta.docHelper.payload && action.meta.docHelper.payload.dockey === 'clientrequest') {
			let userName = store.getState().userPreferences.firstName + ' ' + store.getState().userPreferences.lastName;
			// need to associated to request
			clientRequestDocumentAssociation(action.meta.docHelper.payload, message, userName);
		}

		if (docData) {
			// if this is for collabrotion, then we need to associate this to client request
			// based on dockey
			if (docData.dockey === 'clientrequest') {
				// invoke association call
				clientRequestDocumentAssociation(docData, message);
			} else {
				// For open, edit save scenario we don't need to associate document to task because is saving opened task document
				taskDocumentAssociation(docData, message);
			}

			if (isUserUpload) {
				saveDocumentChangeReason(docData, message);
			}
		}

		return next(updatedAction);
	};

	/**
	 * GLAccount Document Association dispatches the action to associate uploaded document to glAccount
	 * This has been moved into the middleware so that it can be called after the uploadComplete Hub method is called.
	 * @param {Object} docData - id of the first uploaded document
	 */
	let isAssociatingGLAccount = false;
	const associateGLAccount = async (docData) => {
		if (isAssociatingGLAccount) {
			return; // Exit if the function is already executing
		}
		isAssociatingGLAccount = true; // Set the lock
		try {
			const engagementId = store.getState().engagement?.engagementId;
			const glAccount = store.getState().glAccounts;
			const isAssociated = isGLAccountAssociated(glAccount);
			if (!isAssociated) {
				store.dispatch(updateGLAccountUploadingCount(counterOperation.increment));
				await store.dispatch(
					addDocumentToGLAccount(engagementId, glAccount.trialBalanceId, glAccount.id, docData.documentId)
				);
				const newDoc = {
					documentId: docData.documentId,
					documentName: docData.documentName,
					documentTypeId: docData.documentType,
					glAccountId: glAccount.id,
					sourceEngagementId: docData.sourceEngagementId
				};
				store.dispatch(modifyGLAccountDocument(glAccount.trialBalanceId, newDoc, reducerOperations.Add));
			}
		} finally {
			store.dispatch(updateGLAccountUploadingCount(counterOperation.decrement));
			isAssociatingGLAccount = false; // Release the lock
		}
	};

	const isGLAccountAssociated = (glAccount) => {
		const glAccountDocuments = store.getState().trialBalances?.byId[glAccount.trialBalanceId]?.glAccountDocuments;
		const filteredDocuments = glAccountDocuments?.filter((doc) => doc.glAccountId === glAccount.id);
		const isAlreadyAssociated = filteredDocuments?.some((doc) => doc.isCompleted);
		return isAlreadyAssociated;
	};

	/**
	 * This is a SignalR hub client method, which is triggered when there is a uplaod Error
	 * @param {Array} message - Error object passed from DocHelper has a unique format of Array of stirngs
	 * @param {String} Id - id
	 * @returns {Object} an action
	 */
	const uploadError = function(message, Id) {
		console.log('📃: Upload Error ❌', message, Id);
		return next({
			type: 'API_ERROR',
			error: message
		});
	};

	/**
	 * This is the SignalR hub client method, which is called on regular intervals during upload Progress
	 * @param {Object} message - contains details of Progress for the document being uploaded
	 * Here we dispatch the Progress action to handle the progress indicator with redux store update
	 * @returns {Object} the next action.
	 */
	const uploadProgress = function(message) {
		if (Number(message?.EngagementId) !== Number(store.getState().engagement?.engagementId)) {
			return;
		}
		console.log('📃: Upload Progress ⚡⚡⚡⚡⚡', message);
		console.log('Printing client request id as well => ', action?.meta?.docHelper?.docPayload[0]?.relatedEntityId);
		let updatedAction = Object.assign({}, action, {
			type: 'DOCUMENT_UPLOAD_PROGRESS',
			meta: {
				docHelper: {
					payload: Object.assign({}, store.getState().docHelperDocuments[message.FileName], {
						documentId: message.FileId,
						inProgress: message.ProgressPercent !== 100,
						progressPercent: message.ProgressPercent,
						relatedEntityId: action?.meta?.docHelper?.docPayload[0]?.relatedEntityId
					}),
					message: message
				}
			}
		});
		return next(updatedAction);
	};

	const addDocumentError = function() {
		var args = Array.prototype.slice.call(arguments);

		console.log('📃❌❌❌❌❌add Document Error: ', args);

		if (args[0] === HttpStatusCodes.PreConditionFailed) {
			return next({
				type: ADD_DUPLICATE_DOCUMENT,
				meta: {
					document: {
						documentName: args[1],
						documentId: args[4],
						docTypeId: args[5],
						engagementId: args[6],
						payload: store.getState().docHelperDocuments[args[1]],
						data: [store.getState().docHelperDocuments[args[1]]?.filePath]
					}
				}
			});
		} else {
			// Adding timeout as because dochelper retrurn 200 for API Error
			setTimeout(() => {
				next({
					type: 'REMOVE_TASK_DOCUMENT',
					meta: action.meta,
					documentName: args[1]
				});
			}, 3000);

			return next({
				type: 'DOCHELPER_ERROR',
				error: args
			});
		}
	};

	const downloadProgress = function(message, inProgress) {
		console.log('⚡⚡⚡⚡⚡📃: download Progress', message.ProgressPercent);
		//turnOffDownloadListeners();
		let updatedAction = Object.assign({}, action, {
			type: 'DOCUMENT_DOWNLOAD_PROGRESS',
			meta: {
				docHelper: {
					payload: Object.assign({}, action.meta.docHelper.payload, {
						inProgress: inProgress ? inProgress : message.ProgressPercent !== 100,
						progressPercent: message.ProgressPercent
					}),
					message: message
				}
			}
		});
		return next(updatedAction);
	};

	const downloadComplete = function(message) {
		console.log('✔📃: Download Complete', message, 'action:', action);
		//turnOffDownloadListeners();
		_.delay(
			function() {
				//Make some delay to hide progress bar and to show progress because some time download progress evet is not trigered by doc helper
				// if the file is already downloaded on user machine or file size is very small
				let updatedAction = Object.assign({}, action, {
					type: 'DOCUMENT_DOWNLOAD_COMPLETE',
					meta: {
						docHelper: {
							payload: Object.assign({}, action.meta.docHelper.payload, {
								inProgress: false
							}),
							message: message
						}
					}
				});
				return next(updatedAction);
			},
			1000,
			message
		);

		// complete is triggered in delay
		downloadProgress(message, true);
	};

	const downloadCanceled = function(message) {
		console.log('❌📃: download Canceled', message);
		return next({
			type: 'API_ERROR',
			error: message
		});
	};

	const downloadError = (message) => {
		console.log('📃❌❌❌❌❌Download Error: ', message);
		return next({
			type: 'DOCHELPER_ERROR',
			error: [message]
		});
	};

	//Check for config availability
	if (!config) {
		const configMsg = `
        Config unavailable.
        The AD package requires config to get the authorization token for the api call.`;
		console.log(configMsg);
	}

	if (action.type === 'DOCHELPER_DOWNLOAD_DOCUMENT') {
		if (!sessionStorage.getItem('docHubDownloadCallbacksRegistered')) {
			sessionStorage.setItem('docHubDownloadCallbacksRegistered', true);
			// Registering documentHub events with Listeners
			$.connection[action.docHelperConfig.hubName].on('downloadComplete', downloadComplete);
			$.connection[action.docHelperConfig.hubName].on('downloadProgress', downloadProgress);
			$.connection[action.docHelperConfig.hubName].on('downloadCanceled', downloadCanceled);
			$.connection[action.docHelperConfig.hubName].on('downloadError', downloadError);
		}
	}

	// For DocHelper Upload we need to create an array for paths
	if (action.type === 'DOCHELPER_UPLOAD_DOCUMENT') {
		if (!sessionStorage.getItem('docHubUploadCallbacksRegistered')) {
			sessionStorage.setItem('docHubUploadCallbacksRegistered', true);
			$.connection[action.docHelperConfig.hubName].on('uploadProgress', uploadProgress);
			$.connection[action.docHelperConfig.hubName].on('uploadComplete', uploadComplete);
			$.connection[action.docHelperConfig.hubName].on('uploadError', uploadError);
			$.connection[action.docHelperConfig.hubName].on('addDocumentError', addDocumentError);
		}

		if (action.type === 'DOCHELPER_UPLOAD_DOCUMENT' && action?.response?.length === 0) {
			store.dispatch(updateGLAccountUploadingCount(counterOperation.decrement));
		}
		if (action.response) {
			action.meta.docHelper.data = action.response.map((item) => {
				return item.filePath;
			});
		} else {
			console.log('action.response is null', action);
		}
	}

	var setDochelperState = (duplicateDocs) => {
		return new Promise((resolve) => {
			axios({
				method: 'PUT',
				url: `${action.docHelperConfig.docHelperApiUri}onlinestate?state=${
					action.isOnline ? true : false
				}&docUrl=${env.getDocServiceUrl()}&resourceUri=${env.getURL('Engg_AuthAudience_URL')}`
			}).then(() => {
				return resolve(duplicateDocs);
			});
		});
	};

	const associateOfflineTaskDocuments = (duplicateDocs) => {
		let {method, url, data, headers} = action.meta.docHelper;
		if (!action.isOnline && duplicateDocs && duplicateDocs.length) {
			let duplicateFilePaths = _.map(duplicateDocs, 'filePath');
			data = data.filter((d) => {
				return !duplicateFilePaths.includes(d);
			});
		}

		if (action.response) {
			var payload = action.response.filter((res) => {
				return data.includes(res.filePath);
			});

			action.meta.docHelper.docPayload = payload.map((f) => {
				return Object.assign({}, action.meta.docHelper.payload, {
					key: f.name,
					id: f.name,
					filename: f.name,
					documentName: f.name.split(f.fileExtension)[0],
					fileExt: f.fileExtension.split('.')[1],
					size: f.size,
					filePath: f.filePath
				});
			});
		}
		return axios({method, url, data, headers});
	};

	(async function offlineDuplicateDocCheck() {
		let duplicateDocs;
		if (!action.isOnline && action.response) {
			let engagementIdFragment =
				action.meta.api.url.toLowerCase().indexOf('/engagements/') > -1
					? action.meta.api.url.toLowerCase().split('/engagements/')[1]
					: action.meta.api.url.toLowerCase().split('engagementid=')[1];
			let engagementId = engagementIdFragment.split('/')[0].split('&')[0];

			let offlineEvidences = await idbKeyval.get(engagementId + '_' + 'offlineEvidences');
			if (offlineEvidences && offlineEvidences.documents.length) {
				duplicateDocs = action.response.filter((el) => {
					return offlineEvidences.documents.some((f) => {
						return f.documentName + '.' + f.fileExt === el.name;
					});
				});
				console.log('offline duplicate documents', duplicateDocs);
			}
		}
		return duplicateDocs;
	})()
		.then((duplicateDocs) => {
			if (duplicateDocs && duplicateDocs.length > 0) {
				duplicateDocs.map((doc) => {
					let args = [];
					args.push(412);
					args.push(doc.name);
					return next({
						type: 'DOCHELPER_ERROR',
						error: args
					});
				});
			}
			setDochelperState(duplicateDocs)
				.then(associateOfflineTaskDocuments)
				.then((response) => {
					action.response = response.data;
					if (!action.isOnline) {
						// In offline mode docHelper will not send signalR events
						// Since TaskDocument association is part of hub client methods - uploadComplete
						// it will not happen in offline mode
						// For Offline, we need to trigger task document association after the docHelper response.
						let offlineDocsToAssociate = [];
						action.response.forEach((item, index) => {
							let rObj = action.responseObj.data[index];
							let docName = rObj.name.split(rObj.fileExtension)[0];
							let docExt = rObj.fileExtension.split('.')[1];
							let engagementId = qs.parse(window.location.search.toLowerCase()).engagementid;
							let payload = action.meta.docHelper.payload;
							let taskArr = [];
							taskArr.push({id: payload.taskId, taskTypeId: payload.taskTypeId});

							let data = {
								TaskId: payload.taskId,
								DocumentId: item.DocumentTransactionId,
								DocumentTypeId: payload.documentType,
								DocumentName: docName,
								Documentfileext: docExt,
								EngagementId: engagementId,
								SourceEngagementID: engagementId,
								workspaceId: payload.workspaceId,
								taskTypeId: payload.taskTypeId,
								parentName: payload.parentName,
								operationType: 1,
								dockey: payload.dockey,
								approvals: [],
								documentReviews: [],
								clientApprovals: [],
								tasks: taskArr
							};
							offlineDocsToAssociate.push(data);
						});
						offlineTaskDocumentAssociation(offlineDocsToAssociate);
					}
					return next(action);
				})
				.catch((error) => {
					action.error = error;
					action.response = {};
					action.responseObj = {};
					return next({type: API_ERRORS, error});
				});
		})
		.catch((error) => {
			action.error = error;
			action.response = {};
			action.responseObj = {};
			return next({type: API_ERRORS, error});
		});
};

export default docHelperMiddleware;
